import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Sales.css";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "https://techiohisab.com/api";
const SALES_API = `${API_BASE}/sales`;
const TRACKING_COUNTS_API = `${API_BASE}/settlement-reports/tracking-seller-counts`;
const SALES_ORDERS_STORAGE_KEY = "sales_orders_history";

const todayDate = new Date().toISOString().slice(0, 10);

const DEFAULT_ORDER = {
  sale_date: todayDate,
  platform: "DIRECT",
  account_name: "",
  unit: "",
};

const createOrderRow = () => ({
  rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ...DEFAULT_ORDER,
});

const PLATFORM_OPTIONS = ["DIRECT", "FLIPKART", "AMAZON", "MEESHO", "WEBSITE"];
const BLOCKED_STATUSES = new Set(["Cancel", "Cancelled", "Canceled", "Return", "RTO", "DTO"]);

const normalizePlatformValue = (platform) =>
  String(platform || "DIRECT").trim().toUpperCase() || "DIRECT";

const normalizeAccountValue = (account) =>
  String(account || "-").trim().toLowerCase() || "-";

const normalizeStatus = (status = "") => {
  const raw = String(status || "").trim();
  if (!raw) return "Packed";
  if (raw.toLowerCase() === "cancelled" || raw.toLowerCase() === "canceled") return "Cancel";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const dateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const getSaleQty = (row) => {
  const qty = toNumber(row?.quantity || row?.qty || row?.total_qty || row?.total_unit);
  if (qty > 0) return qty;

  const labelQty = String(row?.items || "")
    .split(",")
    .map((item) => {
      const match = item.match(/\((\d+(?:\.\d+)?)\)\s*$/);
      return match ? toNumber(match[1]) : 0;
    })
    .reduce((sum, qtyValue) => sum + qtyValue, 0);

  return labelQty > 0 ? labelQty : 1;
};

const makeKey = ({ sale_date, platform, account_name }) =>
  `${dateOnly(sale_date)}|${normalizePlatformValue(platform)}|${normalizeAccountValue(account_name)}`;

const makeFileKey = ({ sale_date, account_name }) =>
  `${dateOnly(sale_date)}|${normalizeAccountValue(account_name)}`;

const loadSalesOrders = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SALES_ORDERS_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-IN");
};

export default function SalesOrders() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [sales, setSales] = useState([]);
  const [trackingSellerCounts, setTrackingSellerCounts] = useState([]);
  const [orderRows, setOrderRows] = useState(() => [createOrderRow()]);
  const [orders, setOrders] = useState(loadSalesOrders);
  const [editId, setEditId] = useState(null);
  const [differenceEditIds, setDifferenceEditIds] = useState([]);
  const [showDifference, setShowDifference] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(todayDate);
  const [filterToDate, setFilterToDate] = useState(todayDate);
  const [filterAccount, setFilterAccount] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/accounts`).catch(() => ({ data: [] })),
      axios.get(SALES_API).catch(() => ({ data: [] })),
      axios.get(TRACKING_COUNTS_API).catch(() => ({ data: [] })),
    ]).then(([accountsRes, salesRes, trackingCountsRes]) => {
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
      setTrackingSellerCounts(Array.isArray(trackingCountsRes.data) ? trackingCountsRes.data : []);
    });
  }, []);

  const getPlatformAccounts = (platform) => {
    const selectedPlatform = normalizePlatformValue(platform);
    return accounts.filter(
      (account) => normalizePlatformValue(account.platform) === selectedPlatform
    );
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((row) => {
      if (filterPlatform && row.platform !== filterPlatform) return false;
      if (filterAccount && row.account_name !== filterAccount) return false;
      if (filterFromDate && row.sale_date < filterFromDate) return false;
      if (filterToDate && row.sale_date > filterToDate) return false;
      return true;
    });
  }, [orders, filterPlatform, filterFromDate, filterToDate, filterAccount]);

  const actualSalesByKey = useMemo(() => {
    const map = new Map();

    sales
      .filter((row) => !BLOCKED_STATUSES.has(normalizeStatus(row?.status || row?.tracking_status)))
      .forEach((row) => {
        const key = makeKey({
          sale_date: row.sale_date || row.created_at,
          platform: row.platform || "DIRECT",
          account_name: row.account_name || row.customer_name || "-",
        });
        const current = map.get(key) || {
          sale_date: dateOnly(row.sale_date || row.created_at),
          platform: normalizePlatformValue(row.platform || "DIRECT"),
          account_name: row.account_name || row.customer_name || "-",
          actualUnit: 0,
        };
        current.actualUnit += getSaleQty(row);
        map.set(key, current);
      });

    return map;
  }, [sales]);

  const cancelledSalesByKey = useMemo(() => {
    const map = new Map();
    sales
      .filter((row) => BLOCKED_STATUSES.has(normalizeStatus(row?.status || row?.tracking_status)))
      .forEach((row) => {
        const key = makeKey({
          sale_date: row.sale_date || row.created_at,
          platform: row.platform || "DIRECT",
          account_name: row.account_name || row.customer_name || "-",
        });
        map.set(key, (map.get(key) || 0) + getSaleQty(row));
      });
    return map;
  }, [sales]);

  const fileUnitsByAccount = useMemo(() => {
    const map = new Map();
    trackingSellerCounts.forEach((row) => {
      const key = makeFileKey({ sale_date: row.report_date, account_name: row.seller_name });
      const current = map.get(key) || {
        sale_date: dateOnly(row.report_date),
        account_name: row.seller_name || "-",
        fileUnit: 0,
      };
      current.fileUnit += toNumber(row.file_unit);
      map.set(key, current);
    });
    return map;
  }, [trackingSellerCounts]);

  const differenceRows = useMemo(() => {
    const plannedMap = new Map();

    filteredOrders.forEach((row) => {
      const key = makeKey(row);
      const current = plannedMap.get(key) || {
        sale_date: row.sale_date,
        platform: row.platform,
        account_name: row.account_name || "-",
        expectedUnit: 0,
      };
      current.expectedUnit += toNumber(row.unit);
      plannedMap.set(key, current);
    });

    actualSalesByKey.forEach((actual, key) => {
      const inFilter =
        (!filterPlatform || actual.platform === filterPlatform) &&
        (!filterAccount || actual.account_name === filterAccount) &&
        (!filterFromDate || actual.sale_date >= filterFromDate) &&
        (!filterToDate || actual.sale_date <= filterToDate);

      if (inFilter && !plannedMap.has(key)) {
        plannedMap.set(key, {
          sale_date: actual.sale_date,
          platform: actual.platform,
          account_name: actual.account_name,
          expectedUnit: 0,
        });
      }
    });

    fileUnitsByAccount.forEach((fileRow, fileKey) => {
      const inFilter =
        (!filterAccount || normalizeAccountValue(fileRow.account_name) === normalizeAccountValue(filterAccount)) &&
        (!filterFromDate || fileRow.sale_date >= filterFromDate) &&
        (!filterToDate || fileRow.sale_date <= filterToDate);
      if (inFilter && !Array.from(plannedMap.values()).some((row) => makeFileKey(row) === fileKey)) {
        plannedMap.set(`${fileKey}|DIRECT`, {
          sale_date: fileRow.sale_date,
          platform: "DIRECT",
          account_name: fileRow.account_name,
          expectedUnit: 0,
        });
      }
    });

    return Array.from(plannedMap.entries())
      .map(([key, planned]) => {
        const actual = actualSalesByKey.get(key);
        const actualUnit = toNumber(actual?.actualUnit);
        const expectedUnit = toNumber(planned.expectedUnit);
        const fileUnit = toNumber(fileUnitsByAccount.get(makeFileKey(planned))?.fileUnit);
        return {
          ...planned,
          actualUnit,
          cancelledUnit: toNumber(cancelledSalesByKey.get(key)),
          fileUnit,
          actualDifference: actualUnit - expectedUnit,
          fileDifference: fileUnit - expectedUnit,
        };
      })
      .filter((row) => row.actualDifference !== 0 || row.fileDifference !== 0 || row.cancelledUnit !== 0)
      .sort((a, b) => String(b.sale_date).localeCompare(String(a.sale_date)));
  }, [actualSalesByKey, cancelledSalesByKey, fileUnitsByAccount, filterAccount, filterFromDate, filterPlatform, filterToDate, filteredOrders]);

  const saveOrders = (nextOrders) => {
    setOrders(nextOrders);
    localStorage.setItem(SALES_ORDERS_STORAGE_KEY, JSON.stringify(nextOrders));
  };

  const resetForm = () => {
    setOrderRows([createOrderRow()]);
    setEditId(null);
    setDifferenceEditIds([]);
  };

  const addOrUpdateOrder = (event) => {
    event.preventDefault();
    const rowsToSave = (editId ? orderRows.slice(0, 1) : orderRows)
      .map((row) => ({
        ...row,
        sale_date: row.sale_date || "",
        platform: row.platform || "DIRECT",
        account_name: row.account_name || "",
        unit: toNumber(row.unit),
      }))
      .filter((row) => row.sale_date || row.platform || row.account_name || row.unit > 0);

    if (rowsToSave.length === 0) {
      alert("At least one sale order row required");
      return;
    }
    if (rowsToSave.some((row) => !row.sale_date)) {
      alert("Date required");
      return;
    }
    if (rowsToSave.some((row) => !row.platform)) {
      alert("Platform required");
      return;
    }
    if (rowsToSave.some((row) => row.unit <= 0)) {
      alert("Unit must be greater than 0");
      return;
    }

    const now = new Date().toISOString();

    if (differenceEditIds.length > 0) {
      const row = rowsToSave[0];
      const original = orders.find((orderItem) => differenceEditIds.includes(orderItem.id));
      const nextOrder = {
        id: original?.id || Date.now(),
        sale_date: row.sale_date,
        platform: row.platform,
        account_name: row.account_name,
        unit: row.unit,
        created_at: original?.created_at || now,
        updated_at: now,
      };
      saveOrders([
        nextOrder,
        ...orders.filter((orderItem) => !differenceEditIds.includes(orderItem.id)),
      ]);
      resetForm();
      return;
    }

    if (editId) {
      const row = rowsToSave[0];
      const nextOrder = {
        id: editId,
        sale_date: row.sale_date,
        platform: row.platform,
        account_name: row.account_name,
        unit: row.unit,
        created_at: orders.find((orderItem) => orderItem.id === editId)?.created_at || now,
        updated_at: now,
      };
      saveOrders(orders.map((orderItem) => (orderItem.id === editId ? nextOrder : orderItem)));
      resetForm();
      return;
    }

    const nextOrders = rowsToSave.map((row, index) => ({
      id: Date.now() + index,
      sale_date: row.sale_date,
      platform: row.platform,
      account_name: row.account_name,
      unit: row.unit,
      created_at: now,
      updated_at: now,
    }));

    saveOrders([...nextOrders, ...orders]);
    resetForm();
  };

  const editOrder = (row) => {
    setEditId(row.id);
    setOrderRows([{
      rowId: `${row.id}-edit`,
      sale_date: row.sale_date || todayDate,
      platform: row.platform || "DIRECT",
      account_name: row.account_name || "",
      unit: row.unit || "",
    }]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateOrderRow = (rowId, patch) => {
    setOrderRows((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
  };

  const addOrderRow = () => {
    setOrderRows((prev) => [...prev, createOrderRow()]);
  };

  const removeOrderRow = (rowId) => {
    setOrderRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.rowId !== rowId)));
  };

  const deleteOrder = (id) => {
    if (!window.confirm("Delete this sale order?")) return;
    saveOrders(orders.filter((row) => row.id !== id));
    if (editId === id) resetForm();
  };

  const getDifferenceOrderIds = (differenceRow) =>
    orders
      .filter((order) => makeKey(order) === makeKey(differenceRow))
      .map((order) => order.id);

  const editDifferenceRow = (differenceRow) => {
    const ids = getDifferenceOrderIds(differenceRow);
    if (ids.length === 0) return;
    setDifferenceEditIds(ids);
    setEditId(null);
    setOrderRows([{
      rowId: `difference-${ids[0]}`,
      sale_date: differenceRow.sale_date || todayDate,
      platform: differenceRow.platform || "DIRECT",
      account_name: differenceRow.account_name === "-" ? "" : differenceRow.account_name || "",
      unit: differenceRow.expectedUnit || "",
    }]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDifferenceRow = (differenceRow) => {
    const ids = getDifferenceOrderIds(differenceRow);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} expected sale order row(s) for this difference?`)) return;
    saveOrders(orders.filter((order) => !ids.includes(order.id)));
    if (differenceEditIds.some((id) => ids.includes(id))) resetForm();
  };

  const resetFilters = () => {
    setFilterPlatform("");
    setFilterFromDate(todayDate);
    setFilterToDate(todayDate);
    setFilterAccount("");
  };

  return (
    <div className="page-container sales-orders-page">
      <div className="purchase-top-bar">
        <h2>Sale Orders</h2>
        <div className="sales-header-actions">
          <button
            type="button"
            className="sales-top-btn sales-top-btn-tax"
            onClick={() => setShowDifference((value) => !value)}
          >
            Difference ({differenceRows.length})
          </button>
          <button
            type="button"
            onClick={() => navigate("/sales")}
            className="sales-top-btn sales-top-btn-history"
          >
            Back to Sales
          </button>
        </div>
      </div>

      <div className="sales-orders-card">
        <form className="sales-orders-form-clean" onSubmit={addOrUpdateOrder}>
          <div className="sales-orders-multi-wrap">
            <table className="inventory-table sales-orders-entry-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Platform</th>
                  <th>Account</th>
                  <th>Unit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orderRows.map((row) => {
                  const rowAccounts = getPlatformAccounts(row.platform);
                  return (
                    <tr key={row.rowId}>
                      <td>
                        <input
                          type="date"
                          value={row.sale_date}
                          onChange={(event) =>
                            updateOrderRow(row.rowId, { sale_date: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={row.platform}
                          onChange={(event) =>
                            updateOrderRow(row.rowId, {
                              platform: event.target.value,
                              account_name: "",
                            })
                          }
                        >
                          {PLATFORM_OPTIONS.map((platform) => (
                            <option key={platform} value={platform}>
                              {platform === "DIRECT"
                                ? "Direct"
                                : platform.charAt(0) + platform.slice(1).toLowerCase()}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={row.account_name}
                          onChange={(event) =>
                            updateOrderRow(row.rowId, { account_name: event.target.value })
                          }
                        >
                          <option value="">Select Account</option>
                          {rowAccounts.map((account) => (
                            <option key={account.id} value={account.account_name}>
                              {account.account_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          placeholder="Unit"
                          value={row.unit}
                          onChange={(event) =>
                            updateOrderRow(row.rowId, { unit: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="delete-btn"
                          disabled={orderRows.length === 1}
                          onClick={() => removeOrderRow(row.rowId)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sales-orders-form-actions">
            {!editId && differenceEditIds.length === 0 && (
              <button type="button" className="add-item-btn" onClick={addOrderRow}>
                + Add Order
              </button>
            )}
            <button type="submit" className="sales-submit-btn">
              {editId || differenceEditIds.length > 0 ? "Update Sale Order" : "Add Sale Orders"}
            </button>
          </div>
          {(editId || differenceEditIds.length > 0) && (
            <button type="button" className="sales-filters-reset-btn" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </form>
      </div>

      <div className="sales-orders-card">
        <div className="sales-orders-filters-clean">
          <select value={filterPlatform} onChange={(event) => setFilterPlatform(event.target.value)}>
            <option value="">All Platforms</option>
            {PLATFORM_OPTIONS.map((platform) => (
              <option key={platform} value={platform}>
                {platform === "DIRECT"
                  ? "Direct"
                  : platform.charAt(0) + platform.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filterFromDate}
            onChange={(event) => setFilterFromDate(event.target.value)}
          />
          <input
            type="date"
            value={filterToDate}
            onChange={(event) => setFilterToDate(event.target.value)}
          />
          <select value={filterAccount} onChange={(event) => setFilterAccount(event.target.value)}>
            <option value="">All Accounts</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.account_name}>
                {account.account_name}
              </option>
            ))}
          </select>
          <button type="button" className="sales-filters-reset-btn" onClick={resetFilters}>
            Reset Filters
          </button>
        </div>
      </div>

      {showDifference && (
        <div className="sales-orders-card">
          <div className="sales-orders-section-head">
            <h3>Difference</h3>
          </div>
          <div className="sales-history-scroll">
            <table className="inventory-table sales-orders-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Platform</th>
                  <th>Account</th>
                  <th>Expected Unit</th>
                  <th>Actual Unit</th>
                  <th>Cancelled Unit</th>
                  <th>File Unit</th>
                  <th>Actual Difference</th>
                  <th>File Difference</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {differenceRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: "center" }}>
                      No difference found
                    </td>
                  </tr>
                ) : (
                  differenceRows.map((row) => (
                    <tr key={`${row.sale_date}-${row.platform}-${row.account_name}`}>
                      <td>{formatDate(row.sale_date)}</td>
                      <td>{row.platform}</td>
                      <td>{row.account_name || "-"}</td>
                      <td>{row.expectedUnit}</td>
                      <td>{row.actualUnit}</td>
                      <td>{row.cancelledUnit}</td>
                      <td>{row.fileUnit}</td>
                      <td className={row.actualDifference > 0 ? "sales-orders-over" : "sales-orders-under"}>
                        {row.actualDifference > 0 ? "+" : ""}
                        {row.actualDifference}
                      </td>
                      <td className={row.fileDifference > 0 ? "sales-orders-over" : "sales-orders-under"}>
                        {row.fileDifference > 0 ? "+" : ""}
                        {row.fileDifference}
                      </td>
                      <td className="sales-history-actions-cell">
                        {getDifferenceOrderIds(row).length > 0 ? <>
                          <button type="button" className="edit-btn" onClick={() => editDifferenceRow(row)}>Edit</button>
                          <button type="button" className="delete-btn" onClick={() => deleteDifferenceRow(row)}>Delete</button>
                        </> : <span>-</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sales-orders-card">
        <div className="sales-orders-section-head">
          <h3>Sale Order History</h3>
          <span>{filteredOrders.length} row(s)</span>
        </div>
        <div className="sales-history-scroll">
          <table className="inventory-table sales-orders-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platform</th>
                <th>Account</th>
                <th>Unit</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No sale orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((row) => (
                  <tr key={row.id}>
                    <td className="sales-history-date-cell">{formatDate(row.sale_date)}</td>
                    <td>{row.platform || "DIRECT"}</td>
                    <td>{row.account_name || "-"}</td>
                    <td>{row.unit || 0}</td>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : "-"}</td>
                    <td className="sales-history-actions-cell">
                      <button type="button" className="edit-btn" onClick={() => editOrder(row)}>
                        Edit
                      </button>
                      <button type="button" className="delete-btn" onClick={() => deleteOrder(row.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
